import { useState, useEffect } from 'react'
import { Head } from '@inertiajs/react'
import { PomodoroProvider, usePomodoro } from '../context/PomodoroContext'
import TaskManager from '../components/Tasks/TaskManager'
import PomodoroGuide from '../components/Content/PomodoroGuide'
import PomodoroTimer from '../components/Pomodoro/PomodoroTimer'
import PomodoroControls from '../components/Pomodoro/PomodoroControls'

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: '番茄钟应用',
  alternateName: 'Pomodoro Timer App',
  description: '专业的番茄工作法时间管理应用，通过25分钟专注工作+5分钟休息的科学循环，帮助您提升工作效率、克服拖延症、提高专注力。',
  url: 'https://asaptodo.com',
  applicationCategory: 'ProductivityApplication',
  operatingSystem: 'Web Browser',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'CNY' },
  author: { '@type': 'Organization', name: 'ASAPTodo Team' },
  publisher: { '@type': 'Organization', name: 'ASAPTodo' },
  inLanguage: 'zh-CN',
  featureList: ['25分钟专注工作计时器', '5分钟休息提醒', '任务管理功能', '工作统计分析', '自定义时长设置', '声音和浏览器通知', '日历视图', '历史记录查看'],
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', reviewCount: '1250', bestRating: '5', worstRating: '1' },
}

export default function Home() {
  // Inject JSON-LD structured data for SEO (client-side).
  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify(structuredData)
    document.head.appendChild(script)
    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <Head title="番茄钟应用 - 专业的番茄工作法时间管理工具" />
      <div className="min-h-screen flex flex-col items-center justify-start bg-gray-100 pt-4">
        <div className="max-w-7xl w-full space-y-4 px-6">
          <PomodoroProvider>
            <PomodoroUI />
          </PomodoroProvider>
          <PomodoroGuide />
        </div>
      </div>
    </>
  )
}

function PomodoroUI() {
  const [taskName, setTaskName] = useState('')
  const { state, setCurrentTaskId, skipToBreak } = usePomodoro()

  const bgColorClass = state.mode === 'work' ? 'bg-red-500' : 'bg-teal-400'

  const handleSelectTask = (taskId: number, taskTitle: string) => {
    setCurrentTaskId(taskId)
    setTaskName(taskTitle)
  }

  return (
    <div className="flex flex-col w-full gap-4">
      <div className={`${bgColorClass} text-white p-6 rounded-xl shadow-lg w-full`}>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center" />
          <div className="text-right flex">
            <span className="text-sm items-center justify-center my-1.5">番茄钟 {state.completedPomodoros}</span>
            {state.mode === 'work' && !state.isActive && (
              <span className="relative group">
                <button className="text-white p-1 rounded-full hover:bg-teal-400 ml-2" onClick={() => skipToBreak()}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </button>
                <div className="absolute z-10 invisible group-hover:visible bg-gray-800 text-white text-xs rounded py-1 px-2 -right-2 top-full mt-1 whitespace-nowrap">
                  进入休息
                </div>
              </span>
            )}
          </div>
        </div>

        <div className="text-center py-8">
          <PomodoroTimer />
          <div className="mt-2 text-sm opacity-80">{taskName ? taskName : '点击下方任务开始专注'}</div>
        </div>

        <PomodoroControls />
      </div>

      <TaskManager onSelectTask={handleSelectTask} />
    </div>
  )
}
